<template>
    <div
        class="a-alert-box pa-5"
        :class="[{ contextual: isContextual, 'width-auto': widthAuto }, type]"
    >
        <div class="top">
            <div class="alert-title">
                <font-awesome-icon :icon="`fas ${typeIcon}`" class="icon" />
                <p>{{ title }}</p>
            </div>
            <div class="actions-holder ml-2">
                <div v-if="canClose" class="alert-close" @click="$emit('close')">
                    <font-awesome-icon icon="far fa-xmark" class="icon grey01--text" />
                </div>
            </div>
        </div>
        <div v-if="message && !isContextual" class="bottom mt-5">
            <p>
                {{ message }}
                <router-link class="link-out" v-if="linkURL && linkText" :to="linkURL">{{ linkText }}</router-link>
            </p>
        </div>
        <slot name="messageData"></slot>
    </div>
</template>

<script setup>
import { computed } from 'vue';
    
const props = defineProps({
    type: {
        type: String,
        default: 'notification',
        required: true,
        validator: (val) => ['notification', 'error', 'caution', 'success'].includes(val),
    },
    title: {
        type: String,
        required: true,
    },
    message: {
        type: String,
        default: '',
    },
    linkURL: {
        type: String,
        default: '',
    },
    linkText: {
        type: String,
        default: '',
    },
    canClose: {
        type: Boolean,
        default: false,
    },
    isContextual: {
        type: Boolean,
        default: false
    },
    widthAuto: {
        type: Boolean,
        default: false,
    },
})

const typeIcon = computed(() => {
    if (props.type === 'error') {
        return 'fa-exclamation-triangle'
    } else if (props.type === 'caution') {
        return 'fa-exclamation-circle'
    } else if (props.type === 'success') {
        return 'fa-check-circle'
    } else {
        return 'fa-bell-exclamation'
    }
})

</script>

<script>
    export default {
        name: 'AonAlertBox'
    }
</script>

<style lang="scss">
.a-alert-box {
    width: 100%;
    border-top: transparent solid 6px;
    border-radius: 4px;

    &.width-auto {
        width: auto;
    }

    &.contextual {
        border: none;
        padding: 13px !important;
        .top {
            p {
                font-weight: normal;
            }
        }
    }

    .top {
        display: flex;
        justify-content: space-between;
        align-content: center;
        align-items: flex-start;
        width: 100%;

        p {
            color: $navy01;
            margin-bottom: 0;
            font-weight: bold;
        }

        .alert-title {
            display: flex;
            align-items: center;
        }

        .alert-close {
            font-size: 20px;
            
            svg {
                color: $navy01;
            }
        }

        .alert-close:hover {
            cursor: pointer;
            opacity: 0.7;
        }
    }

    .icon {
        width: 20px;
        height: 20px;
        margin-right: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        align-self: flex-start;
    }

    .actions-holder {
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }

    &.notification {
        border-color: $tealDark;
        background: $grey06;

        svg {
            color: $tealDark;
        }
    }

    &.success {
        border-color: $success;
        background: $successBackground;

        svg {
            color: $successDark;
        }
    }

    &.caution {
        border-color: $warning;
        background: $warningBackground;

        svg {
            color: $warningDark;
        }
    }

    &.error {
        border-color: $error;
        background: $errorBackground;

        svg {
            color: $errorDark;
        }
    }
}
</style>
